/**
 * Drag handles the correction system owns beyond the landmark spheres: the three edge handles
 * and the centre handle of an editable contour. Drawn like the prediction's points (overlay
 * layer, on top of everything), with the same hover / drag / edit-target look.
 */
import * as THREE from 'three';
import { LAYER_OVERLAY } from '@/theater/Viewer';

const EDGE_DIAMETER_MM = 0.9;
const CENTER_DIAMETER_MM = 0.9;
const sphereGeometry = new THREE.SphereGeometry(0.5, 16, 12);

export interface HandleSpec {
  key: string;
  position: THREE.Vector3; // scene mm
  color: [number, number, number];
  kind: 'edge' | 'center';
}

export class HandleSet {
  readonly group = new THREE.Group();
  readonly handles = new Map<string, THREE.Mesh>();
  private base = new Map<string, { color: THREE.Color; diameter: number }>();

  constructor() {
    this.group.name = 'correction-handles';
    this.group.layers.set(LAYER_OVERLAY);
  }

  /** Replace the drawn handles with `specs` (keeps materials of unchanged keys). */
  sync(specs: HandleSpec[]): void {
    const keep = new Set(specs.map((s) => s.key));
    for (const [key, mesh] of this.handles) {
      if (keep.has(key)) continue;
      this.group.remove(mesh);
      (mesh.material as THREE.Material).dispose();
      this.handles.delete(key);
      this.base.delete(key);
    }
    for (const spec of specs) {
      let mesh = this.handles.get(spec.key);
      const diameter = spec.kind === 'edge' ? EDGE_DIAMETER_MM : CENTER_DIAMETER_MM;
      if (!mesh) {
        const material = new THREE.MeshBasicMaterial({ color: new THREE.Color(...spec.color), transparent: true, opacity: 1, depthTest: false, depthWrite: false });
        mesh = new THREE.Mesh(sphereGeometry, material);
        mesh.name = `handle:${spec.key}`;
        mesh.renderOrder = 23;
        mesh.layers.set(LAYER_OVERLAY);
        mesh.scale.setScalar(diameter);
        this.group.add(mesh);
        this.handles.set(spec.key, mesh);
        this.base.set(spec.key, { color: new THREE.Color(...spec.color), diameter });
      }
      mesh.position.copy(spec.position);
    }
  }

  setState(key: string, state: { hover: boolean; dragging: boolean }): void {
    const mesh = this.handles.get(key);
    const base = this.base.get(key);
    if (!mesh || !base) return;
    mesh.scale.setScalar(base.diameter * (state.dragging ? 1.5 : state.hover ? 1.3 : 1));
    (mesh.material as THREE.MeshBasicMaterial).color.copy(base.color).lerp(new THREE.Color(1, 1, 1), state.dragging || state.hover ? 0.5 : 0);
  }

  dispose(): void {
    for (const mesh of this.handles.values()) (mesh.material as THREE.Material).dispose();
    this.handles.clear();
    this.base.clear();
  }
}
